import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
  Tooltip,
  Stack,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  PersonAdd as PersonAddIcon,
  Send as SendIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  PictureAsPdf as PdfIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ROLES } from '../../constants/roles';
import WorkflowStatusCard from '../common/WorkflowStatusCard';
import WorkflowGuide from '../common/WorkflowGuide';
import BAUpload from '../compliance/BAUpload';
import QuickAuditTrail from '../common/QuickAuditTrail';
import RemarksHistory from '../common/RemarksHistory';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  formatRoute,
  formatRequirementsSummary,
  sectorRequiresBaUpload,
  getStatusColor,
  renderRemarks,
  getAircraftName,
  getPilotNames,
  resolvePilotId,
  getPilotDisplayName,
  renderCrewResponseChips,
  AssignmentDialog,
  ClearanceDialog,
  RequirementsDialog,
  AuditTrailDialog,
  ItineraryDialog,
  SectorPassengerDialog,
  MroEngineerAssignDialog
} from './OperationsBoard-part2';
import CharterHistoryView from './CharterHistoryView';
import TripsMonthCalendar from '../common/TripsMonthCalendar';
import PanelExpandButton, { getCharterCalendarPaperSx, calendarEnlargeDialogPaperSx } from '../common/PanelExpandButton';
import {
  charterTableContainerSx,
  charterHeaderCellSx,
  charterBodyRowSx,
  charterFirstCellSx,
  charterDataCellSx,
  charterPadRowSx
} from '../common/charterTableTheme';
import EngineeringIcon from '@mui/icons-material/Engineering';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import {
  formatDateTime as formatSectorDateTime,
  createDefaultSectorForm,
  canEditSectorAction,
  canCancelSectorAction
} from '../sales/SalesEnquiryUtils';
import TripAndSectorDialogs from '../sales/SalesEnquiry-part2';
import { utcToDisplayDateAndTime, displayDateTimeToUTC } from '../../utils/timezoneUtils';

const formatRoleLabel = (role = '') =>
  role.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const formatDateTime = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  return d.toLocaleString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric', 
    hour: '2-digit', 
    minute: '2-digit' 
  });
};

const getTripStartEnd = (sectorsList = []) => {
  if (!sectorsList.length) {
    return { start: 'N/A', end: 'N/A' };
  }
  const first = sectorsList[0];
  const last = sectorsList[sectorsList.length - 1];
  return {
    start: formatSectorDateTime(first?.departure?.date, first?.departure?.time),
    end: formatSectorDateTime(last?.arrival?.estimatedDate, last?.arrival?.estimatedTime)
  };
};

const formatActorName = (actor) => {
  if (!actor) return 'System';
  if (typeof actor === 'string') return 'Unknown User';
  const name = `${actor.firstName || ''} ${actor.lastName || ''}`.trim();
  return name || actor.email || 'Unknown User';
};

const OperationsBoard = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  // Helper function to build descriptive error messages
  const buildErrorMessage = (action, error) => {
    const errorDetails = error.response?.data;
    
    if (errorDetails?.errors && Array.isArray(errorDetails.errors) && errorDetails.errors.length > 0) {
      const validationErrors = errorDetails.errors.map(err => {
        const field = err.param || err.path || 'field';
        const msg = err.msg || err.message || 'Invalid value';
        return `${field}: ${msg}`;
      }).join('; ');
      return `${action} failed - Validation errors: ${validationErrors}`;
    } else if (errorDetails?.message) {
      return `${action} failed: ${errorDetails.message}`;
    } else if (error.response?.status === 403) {
      return `${action} failed: You do not have permission to perform this action.`;
    } else if (error.response?.status === 401) {
      return `${action} failed: Your session has expired. Please log in again.`;
    } else if (error.response?.status === 429) {
      return `${action} failed: Too many requests. Please wait a moment before trying again.`;
    } else if (error.response?.status >= 500) {
      return `${action} failed: Server error. Please try again later or contact support.`;
    } else {
      return `${action} failed: ${error.message || 'An unexpected error occurred'}`;
    }
  };
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [pilots, setPilots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [requirementsDialogOpen, setRequirementsDialogOpen] = useState(false);
  const [clearanceDialogOpen, setClearanceDialogOpen] = useState(false);
  const [selectedSector, setSelectedSector] = useState(null);
  const [clearanceSector, setClearanceSector] = useState(null);
  const [auditTrailOpen, setAuditTrailOpen] = useState(false);
  const [auditTrailData, setAuditTrailData] = useState(null);
  const [itineraryDialogOpen, setItineraryDialogOpen] = useState(false);
  const [selectedTripForItinerary, setSelectedTripForItinerary] = useState(null);
  const [sectorPassengerDialogOpen, setSectorPassengerDialogOpen] = useState(false);
  const [selectedSectorForPassengers, setSelectedSectorForPassengers] = useState(null);
  const [baUploadDialogOpen, setBaUploadDialogOpen] = useState(false);
  const [sectorForBAUpload, setSectorForBAUpload] = useState(null);

  // Roles that can upload BA test video (CREW when assigned; Ops/AM/SUPER_ADMIN on behalf of crew)
  const BA_UPLOAD_ROLES = [ROLES.CREW, ROLES.LOAN_PILOT, ROLES.EXTERNAL_PILOT, ROLES.OPERATIONS_MANAGER, ROLES.OPERATIONS_EXECUTIVE, ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN];
  const canUploadBA = user?.role && BA_UPLOAD_ROLES.includes(user.role);

  // Trip-level tabs
  const [tripDetailTab, setTripDetailTab] = useState(0);
  const [mainTab, setMainTab] = useState(0);

  // Sector-level tabs
  const [selectedSectorTab, setSelectedSectorTab] = useState(0);
  const [sectorDetailTabValues, setSectorDetailTabValues] = useState({});

  // Assignment form state
  const [assignmentForm, setAssignmentForm] = useState({
    aircraftId: '',
    assignedPilots: [],
    crewWeights: {}, // { [pilotId]: weightKg }; default 70 when missing
    remarks: ''
  });


  // Requirements form state
  const [requirementsForm, setRequirementsForm] = useState({
    fuelKg: '',
    fuelLitres: '',
    altitude: '',
    route: '',
    weather: '',
    remarks: ''
  });
  const [weatherFile, setWeatherFile] = useState(null);
  const [tripKitFiles, setTripKitFiles] = useState([]);
  const [verifyingTripKit, setVerifyingTripKit] = useState(false);

  const [clearanceForm, setClearanceForm] = useState({
    clearanceNumber: '',
    remarks: ''
  });
  const [mroEngineers, setMroEngineers] = useState([]);
  const [mroDialogOpen, setMroDialogOpen] = useState(false);
  const [mroForm, setMroForm] = useState({ assignedMroEngineerId: '', remarks: '' });
  const [assigningMro, setAssigningMro] = useState(false);
  const [openingTlb, setOpeningTlb] = useState(false);
  const charterTablePanelRef = useRef(null);
  const [charterTablePadCount, setCharterTablePadCount] = useState(0);
  const [expandedPanel, setExpandedPanel] = useState(null); // null | 'table' | 'calendar'
  const [sectorDialogOpen, setSectorDialogOpen] = useState(false);
  const [editingSector, setEditingSector] = useState(null);
  const [sectorForm, setSectorForm] = useState(() => createDefaultSectorForm());
  const [displayTimezone, setDisplayTimezone] = useState('Asia/Kolkata');
  // TripAndSectorDialogs always evaluates trip form fields (even when closed).
  // Must include clientContact or render crashes: Cannot read properties of undefined (reading 'email').
  const emptyTripForm = {
    title: '',
    flightType: 'CHARTER',
    clientName: '',
    clientContact: { email: '', phone: '' },
    passengers: [],
    totalWeight: 0,
    baggageWeight: 20,
    specialRequirements: '',
    itineraryDisplayStatus: '',
    itineraryJourneyNotes: '',
    newRemark: '',
    salesTemplateId: 'default',
    requiresBA: true,
    requiresATCClearance: true
  };

  const fetchTrips = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/sales/trips', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrips(response.data);
    } catch (error) {
      console.error('Failed to fetch trips:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTripDetail = async (tripId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/sales/trips/${tripId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTrip(response.data.trip);
      setSectors(response.data.sectors || []);
    } catch (error) {
      console.error('Failed to fetch trip detail:', error);
      setSectors([]);
    }
  };

  const fetchAircraft = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/aircraft', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAircraft(response.data);
    } catch (error) {
      console.error('Failed to fetch aircraft:', error);
    }
  };

  const fetchPilots = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/ops/crew-duty-roster/assignable-pilots', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPilots(response.data);
    } catch (error) {
      console.error('Failed to fetch pilots:', error);
    }
  };

  const fetchMroEngineers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/ops/mro-engineers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMroEngineers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch MRO engineers:', error);
    }
  };

  const openMroDialog = () => {
    if (!selectedTrip) return;
    const engId = selectedTrip.assignedMroEngineerId?._id || selectedTrip.assignedMroEngineerId || '';
    setMroForm({ assignedMroEngineerId: engId, remarks: '' });
    setMroDialogOpen(true);
  };

  const assignMroEngineer = async () => {
    if (!selectedTrip || assigningMro) return;
    try {
      setAssigningMro(true);
      const token = localStorage.getItem('token');
      const payload = {
        assignedMroEngineerId: mroForm.assignedMroEngineerId || null,
        remarks: mroForm.remarks || undefined
      };
      await axios.put(`/api/ops/trips/${selectedTrip._id}/assign-mro-engineer`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTripDetail(selectedTrip._id);
      setMroDialogOpen(false);
      showToast(
        payload.assignedMroEngineerId
          ? 'MRO engineer assigned for charter techlog'
          : 'MRO engineer cleared from charter',
        'success'
      );
    } catch (error) {
      showToast(buildErrorMessage('MRO engineer assignment', error), 'error');
    } finally {
      setAssigningMro(false);
    }
  };

  const openOrViewCharterTlb = async () => {
    if (!selectedTrip || openingTlb) return;
    const tlb = selectedTrip.technicalLogBookId;
    const existingId = tlb?._id || (typeof tlb === 'string' ? tlb : null);
    const tlbActive = Boolean(existingId && tlb?.isActive !== false);
    if (tlbActive) {
      navigate('/technical-log-book', { state: { openEntryId: existingId } });
      return;
    }
    try {
      setOpeningTlb(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/technical-log-book/open-for-trip/${selectedTrip._id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchTripDetail(selectedTrip._id);
      showToast('Technical Log Book opened for charter', 'success');
      navigate('/technical-log-book', { state: { openEntryId: response.data._id } });
    } catch (error) {
      showToast(buildErrorMessage('Open Technical Log Book', error), 'error');
    } finally {
      setOpeningTlb(false);
    }
  };

  const buildAssignPayload = (form = assignmentForm) => ({
    aircraftId: form.aircraftId,
    assignedPilots: form.assignedPilots,
    remarks: form.remarks,
    crewWeights: (form.assignedPilots || []).map(pid => ({
      pilotId: pid,
      weight: Number(form.crewWeights?.[pid]) || 70
    }))
  });

  const assignSector = async (formOverride = null) => {
    if (assigning) return; // Prevent multiple simultaneous requests
    const form = formOverride || assignmentForm;
    try {
      setAssigning(true);
      const token = localStorage.getItem('token');
      const payload = buildAssignPayload(form);
      const response = await axios.put(`/api/ops/sectors/${selectedSector._id}/assign`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTripDetail(selectedTrip._id);
      await fetchTrips();
      setAssignmentDialogOpen(false);
      resetAssignmentForm();
      
      // Show success message
      const sector = response.data;
      const aircraftName = sector.aircraftId?.registration || sector.aircraftId?.tailNumber || 'Unassigned';
      const pilotNames = sector.assignedPilots?.map(p => `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.email || 'Unknown').join(', ') || 'No pilots assigned';
      const charterNumber = selectedTrip.charterNumber || 'N/A';
      showToast(`Sector ${sector.seqNo} assigned successfully! Charter: ${charterNumber} | Aircraft: ${aircraftName} | Pilots: ${pilotNames} | Status: ${sector.status || 'ASSIGNED'}. Crew has been notified.`, 'success');
    } catch (error) {
      console.error('Failed to assign sector:', error);
      showToast(buildErrorMessage('Sector assignment', error), 'error');
    } finally {
      setAssigning(false);
    }
  };

  const handleSectorTabChange = (event, newValue) => {
    setSelectedSectorTab(newValue);
    // Initialize detail tab for the selected sector if not already set
    const sectorId = sectors[newValue]?._id;
    if (sectorId && sectorDetailTabValues[sectorId] === undefined) {
      setSectorDetailTabValues((prev) => ({
        ...prev,
        [sectorId]: 0
      }));
    }
  };

  const handleSectorDetailTabChange = (sectorId, newValue) => {
    setSectorDetailTabValues((prev) => ({
      ...prev,
      [sectorId]: newValue
    }));
  };


  const updateRequirements = async () => {
    const weatherText = (requirementsForm.weather || '').trim();
    if (!weatherText && !weatherFile) {
      showToast('Weather information is required. Please enter weather text or upload a weather briefing file.', 'error');
      return;
    }
    const fuelKgVal = (requirementsForm.fuelKg ?? '').toString().trim();
    if (!fuelKgVal || isNaN(Number(fuelKgVal))) {
      showToast('Planned Fuel Consumption (kg) is required and must be a number.', 'error');
      return;
    }
    const existingTripKitCount = (selectedSector?.tripKitMediaIds && selectedSector.tripKitMediaIds.length) || 0;
    const newTripKitCount = (tripKitFiles && tripKitFiles.length) || 0;
    if (existingTripKitCount === 0 && newTripKitCount === 0) {
      showToast('At least one TripKit file is required. Upload one or more files (PDF, image, TXT, Excel, CSV, or DOC).', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('fuelKg', fuelKgVal);
      if (requirementsForm.fuelLitres != null && requirementsForm.fuelLitres !== '') formData.append('fuelLitres', requirementsForm.fuelLitres);
      if (requirementsForm.altitude !== '') formData.append('altitude', requirementsForm.altitude);
      if (requirementsForm.route !== '') formData.append('route', requirementsForm.route);
      if (requirementsForm.weather !== '') formData.append('weather', requirementsForm.weather);
      const requirementsRemarks = (requirementsForm.remarks ?? '').toString().trim();
      if (requirementsRemarks) formData.append('remarks', requirementsRemarks);
      if (weatherFile) formData.append('weatherFile', weatherFile);
      (tripKitFiles || []).forEach((file) => formData.append('tripKitFile', file));

      const response = await axios.put(
        `/api/ops/sectors/${selectedSector._id}/requirements`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        }
      );
      await fetchTripDetail(selectedTrip._id);
      setRequirementsDialogOpen(false);
      resetRequirementsForm();
      
      // Show success message
      const sector = response.data;
      const charterNumber = selectedTrip.charterNumber || 'N/A';
      const fuelStr = [requirementsForm.fuelKg ? `${requirementsForm.fuelKg} kg` : null, requirementsForm.fuelLitres ? `${requirementsForm.fuelLitres} L` : null].filter(Boolean).join(', ') || 'N/A';
      const statusNote = sector.status === 'READY_FOR_BA'
        ? ' Sector is now ready for BA test.'
        : '';
      showToast(`Flight requirements updated successfully! Charter: ${charterNumber} | Sector ${sector.seqNo} | Planned Fuel: ${fuelStr} | Altitude: ${requirementsForm.altitude || 'N/A'} | Crew has been notified.${statusNote}`, 'success');
    } catch (error) {
      console.error('Failed to update requirements:', error);
      showToast(buildErrorMessage('Flight requirements update', error), 'error');
    }
  };

  const requestATCClearance = async (sectorId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/atc/sectors/${sectorId}/clearance/request`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTripDetail(selectedTrip._id);
      
      // Show success message
      const sector = response.data;
      const charterNumber = selectedTrip.charterNumber || 'N/A';
      showToast(`ATC clearance requested successfully! Charter: ${charterNumber} | Sector ${sector.seqNo} | Status: ${sector.status || 'READY_FOR_ATC'}. Please wait for clearance approval.`, 'success');
    } catch (error) {
      console.error('Failed to request ATC clearance:', error);
      showToast(buildErrorMessage('ATC clearance request', error), 'error');
    }
  };

  const openClearanceDialog = (sector) => {
    setClearanceSector(sector);
    setClearanceForm({
      clearanceNumber: sector.clearanceNumber || '',
      remarks: sector.remarks?.clearance || sector.clearanceRemarks || ''
    });
    setClearanceDialogOpen(true);
  };

  const openBaUploadDialog = (sector) => {
    setSectorForBAUpload(sector);
    setBaUploadDialogOpen(true);
  };

  const uploadBAVideo = async (file, baTestTime) => {
    if (!sectorForBAUpload || !selectedTrip) return;
    if (!baTestTime) {
      throw new Error('BA test time is required');
    }
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('video', file);
    formData.append('baTestTime', baTestTime);
    await axios.post(`/api/compliance/sectors/${sectorForBAUpload._id}/ba/upload`, formData, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
    });
    await fetchTripDetail(selectedTrip._id);
    setBaUploadDialogOpen(false);
    setSectorForBAUpload(null);
    showToast(`BA test video uploaded for Sector ${sectorForBAUpload.seqNo}. Status updated to READY_FOR_ATC.`, 'success');
  };

  const submitClearanceToCrew = async () => {
    if (!clearanceSector) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/atc/sectors/${clearanceSector._id}/clearance/send`, clearanceForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTripDetail(selectedTrip._id);
      setClearanceDialogOpen(false);
      setClearanceSector(null);
      setClearanceForm({ clearanceNumber: '', remarks: '' });
      
      // Show success message
      const sector = response.data;
      const charterNumber = selectedTrip.charterNumber || 'N/A';
      showToast(`ATC clearance sent to crew successfully! Charter: ${charterNumber} | Sector ${sector.seqNo} | Clearance Number: ${clearanceForm.clearanceNumber || 'N/A'} | Status: ${sector.status || 'CLEARED'}. Crew has been notified.`, 'success');
    } catch (error) {
      console.error('Failed to send ATC clearance:', error);
      showToast(buildErrorMessage('Send ATC clearance to crew', error), 'error');
    }
  };

  const resetAssignmentForm = () => {
    setAssignmentForm({
      aircraftId: '',
      assignedPilots: [],
      crewWeights: {},
      remarks: ''
    });
  };


  const resetRequirementsForm = () => {
    setRequirementsForm({ fuelKg: '', fuelLitres: '', altitude: '', route: '', weather: '', remarks: '' });
    setWeatherFile(null);
    setTripKitFiles([]);
  };

  const openAuditTrail = (trip) => {
    setAuditTrailData({
      entityType: 'charter',
      entityId: trip.charterNumber,
      entityName: `${trip.charterNumber} - ${trip.title}`
    });
    setAuditTrailOpen(true);
  };

  const openItineraryDialog = (trip) => {
    setSelectedTripForItinerary(trip);
    setItineraryDialogOpen(true);
  };


  const resetSectorForm = () => {
    setSectorForm(createDefaultSectorForm(1, displayTimezone));
  };

  const openEditSectorDialog = (sector) => {
    if (!sector) return;
    const dep = sector.departure || {};
    const arr = sector.arrival || {};
    const depDt = dep.date ? utcToDisplayDateAndTime(dep.date, displayTimezone) : { date: '', time: '' };
    const arrDt = arr.estimatedDate ? utcToDisplayDateAndTime(arr.estimatedDate, displayTimezone) : { date: '', time: '' };
    setSectorForm({
      seqNo: sector.seqNo ?? 1,
      departure: {
        airport: dep.airport || '',
        date: depDt.date,
        time: depDt.time,
        fullName: dep.fullName || '',
        coordinates: dep.coordinates || '',
        facilityType: dep.facilityType || ''
      },
      arrival: {
        airport: arr.airport || '',
        estimatedDate: arrDt.date,
        estimatedTime: arrDt.time,
        fullName: arr.fullName || '',
        coordinates: arr.coordinates || '',
        facilityType: arr.facilityType || ''
      },
      wakeTurbulence: sector.wakeTurbulence || '',
      flightRules: sector.flightRules || '',
      flightType: sector.flightType || 'CHARTER',
      charterServiceLabel: sector.charterServiceLabel || '',
      itineraryNotes: sector.itineraryNotes || '',
      groundTransferNotes: sector.groundTransferNotes || '',
      departureGroundHandler: {
        contactPerson: sector.departureGroundHandler?.contactPerson || '',
        contactNumber: sector.departureGroundHandler?.contactNumber || ''
      },
      arrivalGroundHandler: {
        contactPerson: sector.arrivalGroundHandler?.contactPerson || '',
        contactNumber: sector.arrivalGroundHandler?.contactNumber || ''
      },
      departureTransport: {
        vehicleDetails: sector.departureTransport?.vehicleDetails || ''
      },
      arrivalTransport: {
        vehicleDetails: sector.arrivalTransport?.vehicleDetails || ''
      },
      remarks: ''
    });
    setEditingSector(sector);
    setSectorDialogOpen(true);
  };

  const updateSector = async () => {
    if (!selectedTrip || !editingSector) return;
    try {
      const token = localStorage.getItem('token');
      const departureDateISO = sectorForm.departure?.date
        ? (displayDateTimeToUTC(
            sectorForm.departure.date.trim(),
            sectorForm.departure?.time || '00:00',
            displayTimezone
          ) ?? undefined)
        : undefined;
      const arrivalDateISO = sectorForm.arrival?.estimatedDate
        ? (displayDateTimeToUTC(
            sectorForm.arrival.estimatedDate.trim(),
            sectorForm.arrival?.estimatedTime || '00:00',
            displayTimezone
          ) ?? undefined)
        : undefined;
      const payload = {
        seqNo: sectorForm.seqNo ?? 1,
        departure: {
          airport: sectorForm.departure?.airport || '',
          date: departureDateISO,
          time: sectorForm.departure?.time || '',
          fullName: sectorForm.departure?.fullName || undefined,
          coordinates: sectorForm.departure?.coordinates || undefined,
          facilityType: sectorForm.departure?.facilityType || undefined
        },
        arrival: {
          airport: sectorForm.arrival?.airport || '',
          estimatedDate: arrivalDateISO,
          estimatedTime: sectorForm.arrival?.estimatedTime || undefined,
          fullName: sectorForm.arrival?.fullName || undefined,
          coordinates: sectorForm.arrival?.coordinates || undefined,
          facilityType: sectorForm.arrival?.facilityType || undefined
        },
        wakeTurbulence: sectorForm.wakeTurbulence || '',
        flightRules: sectorForm.flightRules || '',
        flightType: sectorForm.flightType || 'CHARTER',
        charterServiceLabel: sectorForm.charterServiceLabel || '',
        itineraryNotes: sectorForm.itineraryNotes || '',
        groundTransferNotes: sectorForm.groundTransferNotes || '',
        departureGroundHandler: sectorForm.departureGroundHandler || undefined,
        arrivalGroundHandler: sectorForm.arrivalGroundHandler || undefined,
        departureTransport: sectorForm.departureTransport || undefined,
        arrivalTransport: sectorForm.arrivalTransport || undefined
      };
      if (sectorForm.remarks != null && sectorForm.remarks.trim()) payload.remarks = sectorForm.remarks.trim();
      await axios.put(`/api/sales/trips/${selectedTrip._id}/sectors/${editingSector._id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTripDetail(selectedTrip._id);
      setSectorDialogOpen(false);
      setEditingSector(null);
      resetSectorForm();
      showToast(`Sector ${editingSector.seqNo} updated successfully.`, 'success');
    } catch (error) {
      console.error('Failed to update sector:', error);
      showToast(buildErrorMessage('Sector update', error), 'error');
    }
  };

  const cancelSector = async (sector) => {
    if (!selectedTrip || !sector) return;
    const isCompleted = ['COMPLETED', 'SECTOR_COMPLETED'].includes(sector.status)
      || ['COMPLETED', 'CHARTER_COMPLETED'].includes(selectedTrip.status);
    const confirmMsg = isCompleted
      ? 'Cancel this completed sector? Charter status will be recalculated from remaining sectors.'
      : 'Cancel this sector? If it is the only sector, the charter will be cancelled.';
    if (!window.confirm(confirmMsg)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/sales/trips/${selectedTrip._id}/sectors/${sector._id}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTripDetail(selectedTrip._id);
      showToast(`Sector ${sector.seqNo} cancelled.`, 'success');
    } catch (error) {
      console.error('Failed to cancel sector:', error);
      showToast(buildErrorMessage('Sector cancel', error), 'error');
    }
  };

  const openSectorPassengerDialog = (sector) => {
    setSelectedSectorForPassengers(sector);
    setSectorPassengerDialogOpen(true);
  };

  const openAssignmentDialog = (sector) => {
    setSelectedSector(sector);
    
    // Extract aircraftId - handle both populated object and string ID
    let aircraftId = '';
    if (sector.aircraftId) {
      aircraftId = typeof sector.aircraftId === 'string' ? sector.aircraftId : sector.aircraftId._id;
    }
    
    // Extract assignedPilots IDs - handle both populated objects and string IDs
    let assignedPilots = [];
    if (sector.assignedPilots && sector.assignedPilots.length > 0) {
      assignedPilots = sector.assignedPilots.map(pilot => 
        typeof pilot === 'string' ? pilot : pilot._id
      );
    }
    
    // Crew weights from sector (default 70 kg per pilot when missing)
    const crewWeights = {};
    (sector.crewWeights || []).forEach(c => {
      const id = c.pilotId && (c.pilotId._id || c.pilotId);
      if (id) crewWeights[id] = c.weight != null ? c.weight : 70;
    });
    assignedPilots.forEach(pid => {
      if (crewWeights[pid] == null) crewWeights[pid] = 70;
    });
    
    setAssignmentForm({
      aircraftId,
      assignedPilots,
      crewWeights,
      remarks: sector.remarks?.assignment || ''
    });
    setAssignmentDialogOpen(true);
  };

  const openRequirementsDialog = (sector) => {
    setSelectedSector(sector);
    setRequirementsForm({
      fuelKg: sector.requirements?.fuelKg ?? '',
      fuelLitres: sector.requirements?.fuelLitres ?? sector.requirements?.fuel ?? '',
      altitude: sector.requirements?.altitude || '',
      route: sector.requirements?.route || '',
      weather: sector.requirements?.weather || '',
      remarks: ''
    });
    setWeatherFile(null);
    setTripKitFiles([]);
    setRequirementsDialogOpen(true);
  };

  const removeTripKitFile = async (sectorId, mediaId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/ops/sectors/${sectorId}/tripkit/${mediaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (selectedTrip?._id) await fetchTripDetail(selectedTrip._id);
      showToast('TripKit file removed.', 'success');
    } catch (error) {
      console.error('Failed to remove TripKit file:', error);
      showToast(buildErrorMessage('Remove TripKit file', error), 'error');
    }
  };

  const canVerifyTripKit = user?.role && [
    ROLES.OPERATIONS_MANAGER,
    ROLES.ACCOUNTABLE_MANAGER,
    ROLES.SUPER_ADMIN
  ].includes(user.role);

  const verifyTripKit = async (sectorId) => {
    try {
      setVerifyingTripKit(true);
      const token = localStorage.getItem('token');
      await axios.post(`/api/ops/sectors/${sectorId}/tripkit/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (selectedTrip?._id) await fetchTripDetail(selectedTrip._id);
      showToast('TripKit verified.', 'success');
    } catch (error) {
      console.error('Failed to verify TripKit:', error);
      showToast(buildErrorMessage('Verify TripKit', error), 'error');
    } finally {
      setVerifyingTripKit(false);
    }
  };

  const getTripKitList = (sector) => {
    if (sector?.tripKitMediaIds?.length) return sector.tripKitMediaIds.filter(Boolean);
    if (sector?.tripKitMediaId) return [sector.tripKitMediaId];
    return [];
  };

  // Helper functions are now imported from OperationsBoard-part2.js

  useEffect(() => {
    fetchTrips();
    fetchAircraft();
    fetchPilots();
    fetchMroEngineers();
    const loadClientSettings = async () => {
      try {
        const response = await axios.get('/api/client/settings');
        setDisplayTimezone(response.data.displayTimezone ?? 'Asia/Kolkata');
      } catch {
        // keep default
      }
    };
    loadClientSettings();
  }, []);

  // Reset trip detail tab when a new trip is selected
  useEffect(() => {
    setTripDetailTab(0);
  }, [selectedTrip?._id]);

  // Handle navigation state from notifications
  useEffect(() => {
    if (location.state) {
      const { selectedTripId, selectedSectorId, charterNumber } = location.state;
      
      // If we have a trip ID or charter number, select that trip
      if (selectedTripId) {
        // Find trip by ID
        const findAndSelectTrip = async () => {
          try {
            await fetchTripDetail(selectedTripId);
            // If we have a specific sector ID, scroll to it or highlight it
            if (selectedSectorId) {
              // Sector selection can be handled by scrolling or highlighting
              // For now, just ensure the trip is selected
              setTimeout(() => {
                const sectorElement = document.querySelector(`[data-sector-id="${selectedSectorId}"]`);
                if (sectorElement) {
                  sectorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  sectorElement.style.backgroundColor = 'rgba(25, 118, 210, 0.1)';
                  setTimeout(() => {
                    sectorElement.style.backgroundColor = '';
                  }, 2000);
                }
              }, 500);
            }
          } catch (error) {
            console.error('Failed to fetch trip from navigation state:', error);
            // Try to find by charter number if ID fails
            if (charterNumber) {
              const trip = trips.find(t => t.charterNumber === charterNumber);
              if (trip) {
                await fetchTripDetail(trip._id);
              }
            }
          }
        };
        
        // Wait for trips to load first
        if (trips.length > 0) {
          findAndSelectTrip();
        } else {
          // If trips not loaded yet, wait a bit and try again
          const timer = setTimeout(() => {
            findAndSelectTrip();
          }, 500);
          return () => clearTimeout(timer);
        }
      } else if (charterNumber) {
        // Find trip by charter number
        const findAndSelectTrip = async () => {
          const trip = trips.find(t => t.charterNumber === charterNumber);
          if (trip) {
            await fetchTripDetail(trip._id);
          }
        };
        
        if (trips.length > 0) {
          findAndSelectTrip();
        } else {
          const timer = setTimeout(() => {
            findAndSelectTrip();
          }, 500);
          return () => clearTimeout(timer);
        }
      }
      
      // Clear navigation state after processing
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, location.pathname, trips, navigate]);

  useEffect(() => {
    const el = charterTablePanelRef.current;
    if (!el) return undefined;

    const ROW_HEIGHT = 56;
    const HEADER_HEIGHT = 40;
    const PANEL_PAD_Y = 24; // p: 1.5 on both sides
    const updatePad = () => {
      const available = el.clientHeight - PANEL_PAD_Y - HEADER_HEIGHT;
      const slots = Math.max(0, Math.floor(available / ROW_HEIGHT));
      setCharterTablePadCount(Math.max(0, slots - (trips?.length || 0)));
    };

    updatePad();
    const observer = new ResizeObserver(() => {
      requestAnimationFrame(updatePad);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [trips?.length, mainTab, expandedPanel]);

  const tripTimes = getTripStartEnd(sectors);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Operations Board
      </Typography>
      <WorkflowGuide variant="operations" />
      <Tabs value={mainTab} onChange={(e, v) => setMainTab(v)} sx={{ mb: 2 }}>
        <Tab label="Current Board" />
        <Tab label="Charter History" />
      </Tabs>

      {mainTab === 0 && (
      <Grid container spacing={3}>
        {/* Charter List + Calendar — full width on desktop for responsive Mac layout */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Charter Management</Typography>
              <Paper
                elevation={0}
                sx={getCharterCalendarPaperSx(expandedPanel)}
              >
                {expandedPanel !== 'calendar' && (
                <Box
                  sx={{
                    position: 'relative',
                    minWidth: 0,
                    minHeight: expandedPanel === 'table'
                      ? { xs: '65vh', md: '70vh' }
                      : { xs: 320, md: 0 },
                    overflow: 'hidden',
                    border: '3px solid',
                    borderColor: 'grey.700',
                    borderRadius: 1.5,
                    bgcolor: 'background.paper'
                  }}
                >
                  <Box
                    ref={charterTablePanelRef}
                    sx={{
                      position: {
                        xs: 'relative',
                        md: expandedPanel === 'table' ? 'relative' : 'absolute'
                      },
                      inset: expandedPanel === 'table' ? undefined : { md: 0 },
                      height: {
                        xs: '100%',
                        md: expandedPanel === 'table' ? '70vh' : 'auto'
                      },
                      display: 'flex',
                      flexDirection: 'column',
                      p: 1.5,
                      overflow: 'hidden',
                      minHeight: expandedPanel === 'table'
                        ? { xs: '65vh', md: '70vh' }
                        : { xs: 320, md: 0 }
                    }}
                  >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1,
                      flexShrink: 0
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Charters
                    </Typography>
                    <PanelExpandButton
                      expandedPanel={expandedPanel}
                      panel="table"
                      onChange={setExpandedPanel}
                    />
                  </Box>
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={charterTableContainerSx(true)}
                  >
                    <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={charterHeaderCellSx(true)}>Charter</TableCell>
                          <TableCell sx={charterHeaderCellSx()}>Aircraft</TableCell>
                          <TableCell sx={charterHeaderCellSx()}>Pilots</TableCell>
                          <TableCell sx={charterHeaderCellSx()}>Flight Type</TableCell>
                          <TableCell sx={charterHeaderCellSx()}>Status</TableCell>
                          <TableCell sx={charterHeaderCellSx()}>Created By</TableCell>
                          <TableCell sx={charterHeaderCellSx()}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {trips.map((trip, index) => (
                          <TableRow
                            key={trip._id}
                            hover
                            onClick={() => fetchTripDetail(trip._id)}
                            sx={charterBodyRowSx(index, selectedTrip?._id === trip._id)}
                          >
                            <TableCell sx={charterFirstCellSx(index, selectedTrip?._id === trip._id)}>
                              {trip.charterNumber}
                            </TableCell>
                            <TableCell sx={charterDataCellSx(index)}>
                              {getAircraftName(trip.listAircraftId, aircraft)}
                            </TableCell>
                            <TableCell sx={charterDataCellSx(index)}>
                              {getPilotNames(trip.listAssignedPilotIds || [], pilots)}
                            </TableCell>
                            <TableCell sx={charterDataCellSx(index)}>
                              <Chip 
                                label={trip.flightType || 'CHARTER'} 
                                size="small" 
                                variant="outlined"
                                sx={{ height: '20px', fontSize: '0.7rem' }}
                              />
                            </TableCell>
                            <TableCell sx={charterDataCellSx(index)}>
                              <Box>
                                <Chip 
                                  label={trip.status} 
                                  color={getStatusColor(trip.status)} 
                                  size="small"
                                  sx={{ height: '20px', fontSize: '0.7rem' }}
                                />
                                {trip.pendingApprovals && trip.pendingApprovals.length > 0 && (
                                  <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                                    {trip.pendingApprovals.map((approval, idx) => (
                                      <Chip
                                        key={idx}
                                        label={`⏳ ${formatRoleLabel(approval.role)}`}
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                        sx={{ fontSize: '0.65rem', height: '18px' }}
                                      />
                                    ))}
                                  </Box>
                                )}
                                {trip.approvalStatus && trip.approvalStatus.allApproved && (
                                  <Box sx={{ mt: 0.5 }}>
                                    <Chip
                                      label="✓ Approved"
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      sx={{ fontSize: '0.65rem', height: '18px' }}
                                    />
                                  </Box>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={charterDataCellSx(index)}>
                              {trip.createdBy 
                                ? `${trip.createdBy.firstName || ''} ${trip.createdBy.lastName || ''}`.trim() || trip.createdBy.email || 'Unknown'
                                : 'Unknown'}
                            </TableCell>
                            <TableCell sx={charterDataCellSx(index)}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openAuditTrail(trip);
                                }}
                                title="View Audit Trail"
                              >
                                <HistoryIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openItineraryDialog(trip);
                                }}
                                title="Generate/Download Itinerary"
                              >
                                <PdfIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        {Array.from({ length: charterTablePadCount }).map((_, padIndex) => {
                          const index = (trips?.length || 0) + padIndex;
                          return (
                            <TableRow
                              key={`empty-row-${padIndex}`}
                              sx={charterPadRowSx(index)}
                            >
                              <TableCell sx={charterFirstCellSx(index)}>&nbsp;</TableCell>
                              <TableCell sx={charterDataCellSx(index)} colSpan={6}>&nbsp;</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  </Box>
                </Box>
                )}
                {expandedPanel !== 'table' && (
                <Box
                  sx={{
                    minWidth: 0,
                    maxWidth: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    p: 1.5,
                    border: '3px solid',
                    borderColor: 'grey.700',
                    borderRadius: 1.5,
                    bgcolor: 'background.paper',
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1,
                      flexShrink: 0
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                      Trip Calendar
                    </Typography>
                    <PanelExpandButton
                      expandedPanel={expandedPanel}
                      panel="calendar"
                      onChange={setExpandedPanel}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <TripsMonthCalendar
                      trips={trips}
                      selectedTrip={selectedTrip}
                      onTripClick={fetchTripDetail}
                      aircraft={aircraft}
                      pilots={pilots}
                      embedded
                    />
                  </Box>
                </Box>
                )}
              </Paper>

              <Dialog
                open={expandedPanel === 'calendar'}
                onClose={() => setExpandedPanel(null)}
                maxWidth={false}
                PaperProps={{ sx: calendarEnlargeDialogPaperSx }}
              >
                <DialogTitle
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.25,
                    flexShrink: 0
                  }}
                >
                  <Typography variant="h6" component="span">Trip Calendar</Typography>
                  <PanelExpandButton
                    expandedPanel={expandedPanel}
                    panel="calendar"
                    onChange={setExpandedPanel}
                  />
                </DialogTitle>
                <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                  <TripsMonthCalendar
                    trips={trips}
                    selectedTrip={selectedTrip}
                    onTripClick={(tripId) => {
                      fetchTripDetail(tripId);
                      setExpandedPanel(null);
                    }}
                    aircraft={aircraft}
                    pilots={pilots}
                    embedded
                    enlarged
                  />
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </Grid>

        {/* Sectors Management — full width below charter/calendar */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sectors {selectedTrip && `- ${selectedTrip.title}`}
              </Typography>

              {selectedTrip ? (
                <>
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>Charter Information</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>Charter Details</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedTrip.charterNumber} - {selectedTrip.title}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">Start Time:</Typography>
                        <Typography variant="body2">{tripTimes.start}</Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">End Time:</Typography>
                        <Typography variant="body2">{tripTimes.end}</Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">Flight Type:</Typography>
                        <Chip label={selectedTrip.flightType || 'CHARTER'} size="small" variant="outlined" />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">Status:</Typography>
                        <Chip 
                          label={selectedTrip.status} 
                          color={getStatusColor(selectedTrip.status)} 
                          size="small" 
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">Passengers:</Typography>
                        <Typography variant="body2">{selectedTrip.passengers?.length || 0}</Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">Client Name:</Typography>
                        <Typography variant="body2">{selectedTrip.clientName || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">Client Email:</Typography>
                        <Typography variant="body2">{selectedTrip.clientContact?.email || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">Client Phone:</Typography>
                        <Typography variant="body2">{selectedTrip.clientContact?.phone || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">Created By:</Typography>
                        <Typography variant="body2">
                          {selectedTrip.createdBy 
                            ? `${selectedTrip.createdBy.firstName || ''} ${selectedTrip.createdBy.lastName || ''}`.trim() || selectedTrip.createdBy.email || 'Unknown'
                            : 'Unknown'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">Created At:</Typography>
                        <Typography variant="body2">
                          {selectedTrip.createdAt ? formatDateTime(selectedTrip.createdAt) : 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">Last Updated:</Typography>
                        <Typography variant="body2">
                          {selectedTrip.updatedAt ? formatDateTime(selectedTrip.updatedAt) : 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Special Requirements:</Typography>
                        <Typography variant="body2">
                          {selectedTrip.specialRequirements || 'None'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Charter Techlog (optional)
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                          <Chip
                            size="small"
                            label={
                              selectedTrip.assignedMroEngineerId
                                ? `MRO: ${
                                    typeof selectedTrip.assignedMroEngineerId === 'object'
                                      ? `${selectedTrip.assignedMroEngineerId.firstName || ''} ${selectedTrip.assignedMroEngineerId.lastName || ''}`.trim()
                                        || selectedTrip.assignedMroEngineerId.email
                                      : 'Assigned'
                                  }`
                                : 'MRO: Unassigned'
                            }
                            color={selectedTrip.assignedMroEngineerId ? 'info' : 'default'}
                            variant="outlined"
                          />
                          <Chip
                            size="small"
                            label={
                              selectedTrip.technicalLogBookId
                                && selectedTrip.technicalLogBookId.isActive !== false
                                ? `TLB: Open (${selectedTrip.technicalLogBookId.workflowStatus || 'DRAFT'})`
                                : 'TLB: Not opened'
                            }
                            color={
                              selectedTrip.technicalLogBookId
                                && selectedTrip.technicalLogBookId.isActive !== false
                                ? 'success'
                                : 'default'
                            }
                            variant="outlined"
                          />
                        </Stack>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EngineeringIcon />}
                            onClick={openMroDialog}
                            disabled={['CHARTER_COMPLETED', 'CANCELLED'].includes(selectedTrip.status)}
                          >
                            Assign MRO Engineer
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<MenuBookIcon />}
                            onClick={openOrViewCharterTlb}
                            disabled={
                              openingTlb
                              || ['CHARTER_COMPLETED', 'CANCELLED'].includes(selectedTrip.status)
                              || (!(
                                selectedTrip.technicalLogBookId
                                && selectedTrip.technicalLogBookId.isActive !== false
                              )
                                && !(sectors || []).some((s) => s.aircraftId))
                            }
                          >
                            {selectedTrip.technicalLogBookId
                              && selectedTrip.technicalLogBookId.isActive !== false
                              ? 'View Techlog'
                              : openingTlb
                                ? 'Opening…'
                                : 'Open Techlog'}
                          </Button>
                        </Stack>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Compliance:</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip 
                            label="BA: first sector / pilot change" 
                            size="small" 
                            color="warning"
                            title="BA upload required on the first sector, and again if pilots change on a later sector"
                          />
                          <Chip 
                            label="ATC Required" 
                            size="small" 
                            color="info"
                            title="Mandatory DGCA requirement"
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Approval Status:</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {selectedTrip.pendingApprovals?.map((approval, idx) => (
                            <Chip
                              key={`pending-${approval.role}-${idx}`}
                              label={`Pending ${formatRoleLabel(approval.role)}`}
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          ))}
                          {selectedTrip.approvalStatus?.amApproved && (
                            <Chip
                              key="am-approved"
                              label={
                                selectedTrip.approvalStatus.amApprovedByName
                                  ? `Accountable Manager Approved by ${selectedTrip.approvalStatus.amApprovedByName}`
                                  : 'Accountable Manager Approved'
                              }
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                          {selectedTrip.approvalStatus?.camoApproved && (
                            <Chip
                              key="camo-approved"
                              label={
                                selectedTrip.approvalStatus.camoApprovedByName
                                  ? `CAMO Approved by ${selectedTrip.approvalStatus.camoApprovedByName}`
                                  : 'CAMO Approved'
                              }
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                          {selectedTrip.approvalStatus?.allApproved && !selectedTrip.pendingApprovals?.length && (
                            <Chip
                              key="all-approved"
                              label="All Approvals Complete"
                              size="small"
                              color="success"
                            />
                          )}
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>
                  
                  {/* Trip Detail Tabs */}
                  {selectedTrip && (
                    <Box sx={{ mb: 2 }}>
                      <Tabs
                        value={tripDetailTab}
                        onChange={(e, newValue) => setTripDetailTab(newValue)}
                        sx={{ mb: 2 }}
                      >
                        <Tab label="Workflow Status" />
                        <Tab label="Remarks" />
                        <Tab label="Recent Activity" />
                      </Tabs>

                      {tripDetailTab === 0 && (
                        <WorkflowStatusCard trip={selectedTrip} user={user} />
                      )}

                      {tripDetailTab === 1 && (
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                              Remarks History
                            </Typography>
                            {selectedTrip.remarksHistory && selectedTrip.remarksHistory.length > 0 ? (
                              <RemarksHistory remarksHistory={selectedTrip.remarksHistory} entityType="trip" />
                            ) : (
                              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                                No remarks yet
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      )}

                      {tripDetailTab === 2 && (
                        <QuickAuditTrail
                          entityType="charter"
                          entityId={selectedTrip.charterNumber || selectedTrip._id}
                          entityName={selectedTrip.charterNumber || selectedTrip.title}
                          maxItems={10}
                        />
                      )}
                    </Box>
                  )}
                  
                  {/* Sector Tabs */}
                  {sectors && sectors.length > 0 ? (
                    <Box sx={{ mt: 2 }}>
                      <Tabs
                        value={selectedSectorTab}
                        onChange={handleSectorTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                      >
                        {sectors.map((sector, index) => (
                          <Tab
                            key={sector._id}
                            label={`Sector ${sector.seqNo} - ${formatRoute(sector)}`}
                            icon={
                              <Chip
                                label={sector.status}
                                color={getStatusColor(sector.status)}
                                size="small"
                                sx={{ ml: 1 }}
                              />
                            }
                            iconPosition="end"
                            sx={{
                              backgroundColor: selectedSectorTab === index ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(25, 118, 210, 0.16)',
                                fontWeight: 'bold'
                              },
                              '&:hover': {
                                backgroundColor: selectedSectorTab === index ? 'rgba(25, 118, 210, 0.20)' : 'rgba(0, 0, 0, 0.04)'
                              }
                            }}
                          />
                        ))}
                      </Tabs>

                      {/* Sector Content */}
                      {sectors[selectedSectorTab] && (() => {
                        const sector = sectors[selectedSectorTab];
                        const sectorDetailTab = sectorDetailTabValues[sector._id] ?? 0;

                        return (
                          <Box>
                            {/* Sector Summary Card */}
                            <Card sx={{ mb: 2 }}>
                              <CardContent>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={3}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                      Route
                                    </Typography>
                                    <Typography variant="body1">{formatRoute(sector)}</Typography>
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                      Departure
                                    </Typography>
                                    <Typography variant="body1">
                                      {formatSectorDateTime(sector.departure?.date, sector.departure?.time)}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                      Arrival
                                    </Typography>
                                    <Typography variant="body1">
                                      {formatSectorDateTime(sector.arrival?.estimatedDate, sector.arrival?.estimatedTime)}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                      Aircraft
                                    </Typography>
                                    <Typography variant="body1">{getAircraftName(sector.aircraftId, aircraft)}</Typography>
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                      Pilots
                                    </Typography>
                                    <Typography variant="body1">{getPilotNames(sector.assignedPilots, pilots)}</Typography>
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                      Passengers
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="body1">
                                        {(() => {
                                          const sectorPassengers = sector.passengers?.length || 0;
                                          const tripPassengers = selectedTrip.passengers?.length || 0;
                                          return sectorPassengers > 0 ? sectorPassengers : tripPassengers;
                                        })()}
                                      </Typography>
                                      {(() => {
                                        const canEdit = canEditSectorAction(sector, selectedTrip, user);
                                        const hasPassengers = (sector.passengers?.length || 0) > 0 || (selectedTrip.passengers?.length || 0) > 0;
                                        
                                        if (!hasPassengers && !canEdit) return null;
                                        
                                        return (
                                          <IconButton
                                            size="small"
                                            onClick={() => openSectorPassengerDialog(sector)}
                                            title={canEdit ? "View/Edit Passengers" : "View Passengers"}
                                            disabled={!canEdit}
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        );
                                      })()}
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                      Weight
                                    </Typography>
                                    <Typography variant="body1">
                                      {sector.totalWeight ?? 0} kg
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                      Requirements
                                    </Typography>
                                    <Typography variant="body1">{formatRequirementsSummary(sector)}</Typography>
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                      TripKit
                                    </Typography>
                                    {(() => {
                                      const tripKitList = getTripKitList(sector);
                                      if (tripKitList.length === 0) {
                                        return <Typography variant="body2" color="text.secondary">Not uploaded</Typography>;
                                      }
                                      const verified = !!sector.tripKitVerification?.verified;
                                      return (
                                        <Stack spacing={0.5}>
                                          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                            <Chip
                                              size="small"
                                              label={verified ? 'Verified' : 'Awaiting verification'}
                                              color={verified ? 'success' : 'warning'}
                                            />
                                            <Typography variant="caption" color="text.secondary">
                                              {tripKitList.length} file{tripKitList.length === 1 ? '' : 's'}
                                            </Typography>
                                          </Stack>
                                          {tripKitList.map((media, idx) => {
                                            const url = typeof media === 'object' ? media.fileUrl : null;
                                            const label = url
                                              ? (url.split('/').pop() || `File ${idx + 1}`)
                                              : `File ${idx + 1}`;
                                            return url ? (
                                              <Typography
                                                key={typeof media === 'object' ? media._id : `${idx}`}
                                                component="a"
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                variant="caption"
                                                sx={{ display: 'block' }}
                                              >
                                                {label}
                                              </Typography>
                                            ) : (
                                              <Typography key={idx} variant="caption">{label}</Typography>
                                            );
                                          })}
                                          {canVerifyTripKit && !verified && (
                                            <Button
                                              size="small"
                                              variant="contained"
                                              color="success"
                                              disabled={verifyingTripKit}
                                              onClick={() => verifyTripKit(sector._id)}
                                              sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                                            >
                                              {verifyingTripKit ? 'Verifying…' : 'Verify'}
                                            </Button>
                                          )}
                                        </Stack>
                                      );
                                    })()}
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                      BA
                                    </Typography>
                                    {sectorRequiresBaUpload(sector, sectors) ? (
                                      <Chip
                                        label={sector.baMediaId ? 'BA Uploaded' : 'BA Required'}
                                        size="small"
                                        color={sector.baMediaId ? 'success' : 'warning'}
                                        title={
                                          Number(sector.seqNo) <= 1
                                            ? 'BA required on first sector'
                                            : 'BA required — pilots changed from previous sector'
                                        }
                                      />
                                    ) : (
                                      <Chip
                                        label="BA Not Required"
                                        size="small"
                                        color="default"
                                        variant="outlined"
                                        title="Same pilots as previous sector — BA already covered"
                                      />
                                    )}
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                      Clearance #
                                    </Typography>
                                    <Typography variant="body1">{sector.clearanceNumber || '—'}</Typography>
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                      Wake Turbulence
                                    </Typography>
                                    <Typography variant="body1">{sector.wakeTurbulence || '—'}</Typography>
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                      Flight Rules
                                    </Typography>
                                    <Typography variant="body1">{sector.flightRules || '—'}</Typography>
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                      Crew Responses
                                    </Typography>
                                    <Box>{renderCrewResponseChips(sector, pilots)}</Box>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                      Approvals
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                      {selectedTrip.pendingApprovals?.map((approval, idx) => (
                                        <Chip
                                          key={`sector-pending-${approval.role}-${idx}`}
                                          label={`Pending ${formatRoleLabel(approval.role)}`}
                                          size="small"
                                          color="warning"
                                          variant="outlined"
                                        />
                                      ))}
                                      {selectedTrip.approvalStatus?.amApproved && (
                                        <Chip
                                          key={`sector-am-approved-${sector._id}`}
                                          label={
                                            selectedTrip.approvalStatus.amApprovedByName
                                              ? `Accountable Manager Approved by ${selectedTrip.approvalStatus.amApprovedByName}`
                                              : 'Accountable Manager Approved'
                                          }
                                          size="small"
                                          color="success"
                                          variant="outlined"
                                        />
                                      )}
                                      {selectedTrip.approvalStatus?.camoApproved && (
                                        <Chip
                                          key={`sector-camo-approved-${sector._id}`}
                                          label={
                                            selectedTrip.approvalStatus.camoApprovedByName
                                              ? `CAMO Approved by ${selectedTrip.approvalStatus.camoApprovedByName}`
                                              : 'CAMO Approved'
                                          }
                                          size="small"
                                          color="success"
                                          variant="outlined"
                                        />
                                      )}
                                      {selectedTrip.approvalStatus?.allApproved && !selectedTrip.pendingApprovals?.length && (
                                        <Chip
                                          key={`sector-all-approved-${sector._id}`}
                                          label="All Approvals Complete"
                                          size="small"
                                          color="success"
                                        />
                                      )}
                                    </Stack>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                      {canEditSectorAction(sector, selectedTrip, user) && (
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          startIcon={<EditIcon />}
                                          onClick={() => openEditSectorDialog(sector)}
                                        >
                                          Edit Sector
                                        </Button>
                                      )}
                                      {canCancelSectorAction(sector, selectedTrip, user) && (
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          color="error"
                                          onClick={() => cancelSector(sector)}
                                        >
                                          Cancel Sector
                                        </Button>
                                      )}
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<AssignmentIcon />}
                                        onClick={() => openAssignmentDialog(sector)}
                                      >
                                        Assign
                                      </Button>
                                      {['CREW_ACCEPTED', 'READY_FOR_BA'].includes(sector.status) && (
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          startIcon={<EditIcon />}
                                          onClick={() => openRequirementsDialog(sector)}
                                        >
                                          Requirements
                                        </Button>
                                      )}
                                      {sector.status === 'READY_FOR_BA'
                                        && sectorRequiresBaUpload(sector, sectors)
                                        && canUploadBA && (
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          startIcon={<CloudUploadIcon />}
                                          onClick={() => openBaUploadDialog(sector)}
                                        >
                                          Upload BA
                                        </Button>
                                      )}
                                      {(sector.status === 'READY_FOR_ATC'
                                        || (sector.status === 'READY_FOR_BA'
                                          && (!sectorRequiresBaUpload(sector, sectors) || sector.baMediaId))) && (
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          startIcon={<SendIcon />}
                                          onClick={() => requestATCClearance(sector._id)}
                                        >
                                          Request ATC
                                        </Button>
                                      )}
                                      {sector.status === 'READY_FOR_ATC' && (
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          startIcon={<SendIcon />}
                                          onClick={() => openClearanceDialog(sector)}
                                        >
                                          Send to Crew
                                        </Button>
                                      )}
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<PersonAddIcon />}
                                        onClick={() => openSectorPassengerDialog(sector)}
                                      >
                                        Manage Passengers
                                      </Button>
                                    </Stack>
                                  </Grid>
                                </Grid>
                              </CardContent>
                            </Card>

                            {/* Sector Detail Tabs */}
                            <Tabs
                              value={sectorDetailTab}
                              onChange={(e, newValue) => handleSectorDetailTabChange(sector._id, newValue)}
                              sx={{ mb: 2 }}
                            >
                              <Tab label="Workflow Status" />
                              <Tab label="Remarks History" />
                              <Tab label="Recent Activity" />
                            </Tabs>

                            {sectorDetailTab === 0 && (
                              <WorkflowStatusCard sector={sector} trip={selectedTrip} user={user} />
                            )}

                            {sectorDetailTab === 1 && (
                              <Card>
                                <CardContent>
                                  <Typography variant="subtitle1" gutterBottom>
                                    Remarks History
                                  </Typography>
                                  {sector.remarksHistory && sector.remarksHistory.length > 0 ? (
                                    <RemarksHistory remarksHistory={sector.remarksHistory} entityType="sector" />
                                  ) : (
                                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                                      No remarks yet
                                    </Typography>
                                  )}
                                </CardContent>
                              </Card>
                            )}

                            {sectorDetailTab === 2 && (
                              <Box>
                                <QuickAuditTrail
                                  entityType="sector"
                                  entityId={sector._id}
                                  entityName={`Sector ${sector.seqNo} - ${formatRoute(sector)}`}
                                  maxItems={10}
                                />
                                {sector.statusHistory && sector.statusHistory.length > 0 && (
                                  <Card sx={{ mt: 2 }}>
                                    <CardContent>
                                      <Typography variant="subtitle1" gutterBottom>
                                        Status History
                                      </Typography>
                                      <List>
                                        {sector.statusHistory
                                          .slice()
                                          .reverse()
                                          .map((entry, idx) => (
                                            <ListItem key={idx} sx={{ pl: 0 }}>
                                              <ListItemText
                                                primary={
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Chip
                                                      label={entry.status.replace(/_/g, ' ')}
                                                      size="small"
                                                      color={getStatusColor(entry.status)}
                                                    />
                                                    <Typography variant="caption" color="text.secondary">
                                                      {formatDateTime(entry.changedAt)}
                                                    </Typography>
                                                  </Box>
                                                }
                                                secondary={
                                                  <Box>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                      By: {formatActorName(entry.actorId)}
                                                    </Typography>
                                                    {entry.remarks && (
                                                      <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ mt: 0.5, fontStyle: 'italic' }}
                                                      >
                                                        "{entry.remarks}"
                                                      </Typography>
                                                    )}
                                                  </Box>
                                                }
                                              />
                                            </ListItem>
                                          ))}
                                      </List>
                                    </CardContent>
                                  </Card>
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      })()}
                    </Box>
                  ) : (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary" align="center">
                        No sectors found for this trip
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Select a trip to manage sectors
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
            )}

            {mainTab === 1 && (
              <CharterHistoryView
                pilots={pilots}
                aircraft={aircraft}
                showToast={showToast}
                buildErrorMessage={buildErrorMessage}
              />
            )}

      {/* Dialogs - imported from OperationsBoard-part2.js */}
      <AssignmentDialog
        open={assignmentDialogOpen}
        onClose={() => {
          setAssignmentDialogOpen(false);
        }}
        selectedSector={selectedSector}
        selectedTrip={selectedTrip}
        sectors={sectors}
        user={user}
        assignmentForm={assignmentForm}
        setAssignmentForm={setAssignmentForm}
        aircraft={aircraft}
        pilots={pilots}
        assigning={assigning}
        assignSector={() => assignSector()}
        getStatusColor={getStatusColor}
      />

      <ClearanceDialog
        open={clearanceDialogOpen}
        onClose={() => setClearanceDialogOpen(false)}
        clearanceForm={clearanceForm}
        setClearanceForm={setClearanceForm}
        submitClearanceToCrew={submitClearanceToCrew}
      />

      <RequirementsDialog
        open={requirementsDialogOpen}
        onClose={() => setRequirementsDialogOpen(false)}
        requirementsForm={requirementsForm}
        setRequirementsForm={setRequirementsForm}
        weatherFile={weatherFile}
        setWeatherFile={setWeatherFile}
        tripKitFiles={tripKitFiles}
        setTripKitFiles={setTripKitFiles}
        selectedSector={selectedSector}
        sectors={sectors}
        removeTripKitFile={removeTripKitFile}
        updateRequirements={updateRequirements}
        user={user}
        verifyTripKit={verifyTripKit}
        verifyingTripKit={verifyingTripKit}
      />

      <AuditTrailDialog
        open={auditTrailOpen}
        onClose={() => setAuditTrailOpen(false)}
        auditTrailData={auditTrailData}
      />

      <ItineraryDialog
        open={itineraryDialogOpen}
        selectedTripForItinerary={selectedTripForItinerary}
        onClose={() => {
          setItineraryDialogOpen(false);
          setSelectedTripForItinerary(null);
        }}
      />

      <SectorPassengerDialog
        open={sectorPassengerDialogOpen}
        onClose={() => {
          setSectorPassengerDialogOpen(false);
          setSelectedSectorForPassengers(null);
        }}
        selectedSectorForPassengers={selectedSectorForPassengers}
        sectors={sectors}
        selectedTrip={selectedTrip}
        user={user}
        fetchTripDetail={fetchTripDetail}
        buildErrorMessage={buildErrorMessage}
        showToast={showToast}
      />

      <MroEngineerAssignDialog
        open={mroDialogOpen}
        onClose={() => setMroDialogOpen(false)}
        selectedTrip={selectedTrip}
        mroEngineers={mroEngineers}
        mroForm={mroForm}
        setMroForm={setMroForm}
        assigningMro={assigningMro}
        assignMroEngineer={assignMroEngineer}
      />

      <Dialog open={baUploadDialogOpen} onClose={() => { setBaUploadDialogOpen(false); setSectorForBAUpload(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Upload BA Test Video{sectorForBAUpload ? ` - Sector ${sectorForBAUpload.seqNo}` : ''}</DialogTitle>
        <DialogContent>
          {sectorForBAUpload && (
            <BAUpload
              onUpload={async (file, baTestTime) => {
                try {
                  await uploadBAVideo(file, baTestTime);
                } catch (error) {
                  showToast(buildErrorMessage('BA video upload', error), 'error');
                  throw error;
                }
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBaUploadDialogOpen(false); setSectorForBAUpload(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      <TripAndSectorDialogs
        tripDialogOpen={false}
        setTripDialogOpen={() => {}}
        sectorDialogOpen={sectorDialogOpen}
        setSectorDialogOpen={setSectorDialogOpen}
        editingTrip={null}
        setEditingTrip={() => {}}
        editingSector={editingSector}
        setEditingSector={setEditingSector}
        selectedTrip={selectedTrip}
        sectors={sectors}
        setTrips={setTrips}
        setSelectedTrip={setSelectedTrip}
        setSectors={setSectors}
        fetchTrips={fetchTrips}
        fetchTripDetail={fetchTripDetail}
        tripForm={emptyTripForm}
        setTripForm={() => {}}
        sectorForm={sectorForm}
        setSectorForm={setSectorForm}
        resetTripForm={() => {}}
        resetSectorForm={resetSectorForm}
        handlePassengerUpdate={() => {}}
        updateSector={updateSector}
        displayTimezone={displayTimezone}
      />
    </Box>
  );
};

export default OperationsBoard;
